import { useCabinetUsers, useMe, useUpdateCabinetUser, type User } from '@fidalia/api';
import {
  Alert,
  Avatar,
  Badge,
  Button,
  DataTable,
  DropdownMenu,
  PageHeader,
  Switch,
  type DropdownItem,
  type TableColumn,
} from '@fidalia/ui';
import { createFileRoute, redirect } from '@tanstack/react-router';
import {
  MdBadge,
  MdDelete,
  MdDoneAll,
  MdEdit,
  MdExtension,
  MdGroups,
  MdMoreVert,
  MdPerson,
  MdPersonAdd,
  MdVerifiedUser,
} from 'react-icons/md';
import { useState } from 'react';

import { AccessModal } from '@/features/users/access-modal';
import { DeleteAccessModal } from '@/features/users/delete-access-modal';
import { errorMessage } from '@/lib/auth';

/**
 * Date **et** heure : deux synchronisations du même jour n'ont rien
 * d'exceptionnel, et c'est justement quand les motifs changent deux fois dans
 * la journée qu'on vient lire cette colonne.
 */
const DATE_HEURE = new Intl.DateTimeFormat('fr-FR', { dateStyle: 'short', timeStyle: 'short' });

function dateLisible(iso: string): string {
  return DATE_HEURE.format(new Date(iso));
}

const FONCTION_LABELS: Record<User['fonction'], string> = {
  praticien: 'Praticien',
  assistant: 'Assistant(e) dentaire',
  secretariat: 'Secrétariat',
  gestionnaire: 'Gestionnaire',
};

export const Route = createFileRoute('/_app/utilisateurs')({
  /**
   * Écran réservé aux administrateurs du cabinet. Le lien est déjà masqué pour
   * les comptes secondaires, mais l'URL reste tapable : sans ce garde, la page
   * s'afficherait quand même. `context.me` vient du garde parent, aucun appel
   * supplémentaire.
   */
  beforeLoad: ({ context }) => {
    if (context.me.user.role !== 'admin') throw redirect({ to: '/' });
  },
  component: UtilisateursPage,
});

function UtilisateursPage() {
  const { data: me } = useMe();
  const { data, isPending, error } = useCabinetUsers();
  const updateUser = useUpdateCabinetUser();

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);
  const [deleting, setDeleting] = useState<User | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Seul un admin du cabinet peut gérer les accès : l'API répond 403 sinon.
  const estAdmin = me?.user.role === 'admin';

  /**
   * Tout le cabinet, le compte principal en tête.
   *
   * Il n'a aucune action sur cet écran — ni suppression, ni désactivation, ni
   * rétrogradation, le backend les refuse toutes. Il y figure quand même : la
   * liste des personnes ayant accès au cabinet serait fausse sans lui, et son
   * absence se lisait comme un oubli plutôt que comme une règle. `sort` étant
   * stable, l'ordre du serveur tient pour le reste.
   */
  const acces = [...(data?.items ?? [])].sort((a, b) => Number(b.principal) - Number(a.principal));

  /**
   * Raison pour laquelle les actions sont bloquées, ou `null` si elles sont
   * autorisées. Ces cas reprennent les refus du backend, pour ne pas proposer
   * une action qui reviendrait en erreur.
   */
  const raisonBlocage = (user: User): string | null => {
    // Avant le contrôle de rôle : la raison tient au compte visé, pas à qui
    // le regarde, et elle vaut même pour le responsable lisant sa propre ligne.
    if (user.principal) return 'Le compte principal ne peut être ni modifié ni désactivé';
    if (!estAdmin) return 'Réservé aux administrateurs du cabinet';
    if (user.id === me?.user.id) return 'Vous ne pouvez pas modifier votre propre accès';
    return null;
  };

  const toggleActif = async (user: User, actif: boolean) => {
    setActionError(null);
    try {
      await updateUser.mutateAsync({ id: user.id, actif });
    } catch (cause) {
      setActionError(errorMessage(cause, 'Modification impossible.'));
    }
  };

  const columns: TableColumn<User>[] = [
    {
      key: 'personne',
      title: 'Membre',
      icon: MdPerson,
      cell: (user) => {
        const nom = `${user.prenom} ${user.nom}`.trim();
        return (
          <div className="flex items-center gap-3">
            <Avatar name={nom} size="sm" tone="auto" />
            <div className="flex min-w-0 flex-col">
              <span className="truncate font-medium">
                {nom}
                {user.id === me?.user.id ? (
                  <span className="ml-2 text-xs text-content-subtle">(vous)</span>
                ) : null}
              </span>
              <span className="truncate text-xs text-content-muted">{user.email}</span>
            </div>
          </div>
        );
      },
    },
    {
      key: 'fonction',
      title: 'Fonction',
      icon: MdBadge,
      cell: (user) => <span className="text-content-muted">{FONCTION_LABELS[user.fonction]}</span>,
    },
    {
      key: 'acces',
      title: 'Niveau d’accès',
      icon: MdVerifiedUser,
      /**
       * Trois niveaux, et il n'y en a pas d'autres : le modèle ne connaît que
       * `admin` et `membre`, plus le drapeau `principal`. Ce dernier est
       * administrateur lui aussi, mais l'afficher comme tel effacerait ce qui
       * le distingue — les autres administrateurs ont été promus et peuvent
       * être rétrogradés, lui non.
       */
      cell: (user) =>
        user.principal ? (
          <Badge tone="accent">Principal</Badge>
        ) : user.role === 'admin' ? (
          <Badge tone="brand">Administrateur</Badge>
        ) : (
          <Badge tone="neutral">Compte secondaire</Badge>
        ),
    },
    {
      key: 'doctolib',
      title: 'Extension Doctolib',
      icon: MdExtension,
      /**
       * Sur quel compte Doctolib l'extension de cette personne synchronise, et
       * quand elle l'a fait pour la dernière fois.
       *
       * L'adresse Doctolib n'est presque jamais celle du compte Fidalia — elle
       * appartient au logiciel du praticien, pas au nôtre. C'est justement ce
       * qui la rend utile ici : une synchronisation écrase les motifs de tout
       * le cabinet, et cette colonne dit par quel accès elle est arrivée.
       */
      cell: (user) => {
        if (!user.doctolibSyncAt) {
          return <span className="text-sm text-content-subtle">Jamais synchronisé</span>;
        }

        return (
          <div className="flex min-w-0 flex-col">
            <span className="truncate text-sm text-content">
              {user.doctolibEmail ?? user.doctolibNom ?? 'Compte Doctolib'}
            </span>
            {/* La double coche reprend celle de la surcouche affichée en fin de
                synchronisation : même signe pour le même fait, d'un écran à
                l'autre. Elle porte la couleur, la date reste sobre. */}
            <span className="flex items-center gap-1 text-xs text-content-subtle">
              <MdDoneAll aria-hidden="true" className="size-3.5 shrink-0 text-success" />
              Dernière synchro. {dateLisible(user.doctolibSyncAt)}
            </span>
          </div>
        );
      },
    },
    {
      key: 'statut',
      title: 'Actif',
      align: 'right',
      cell: (user) => {
        const blocage = raisonBlocage(user);
        // `variables` porte la ligne en cours : seule sa bascule tourne.
        const enCours = updateUser.isPending && updateUser.variables?.id === user.id;

        return (
          <Switch
            checked={user.actif}
            onChange={(actif) => void toggleActif(user, actif)}
            label={`${user.actif ? 'Désactiver' : 'Activer'} l’accès de ${user.prenom} ${user.nom}`}
            hideLabel
            disabled={blocage !== null}
            loading={enCours}
            title={blocage ?? undefined}
            className="justify-end"
          />
        );
      },
    },
    {
      key: 'actions',
      align: 'right',
      className: 'w-px',
      cell: (user) => {
        const blocage = raisonBlocage(user);
        if (blocage !== null) return null;

        const items: DropdownItem[] = [
          { label: 'Modifier', icon: <MdEdit />, onSelect: () => setEditing(user) },
          {
            label: 'Supprimer',
            icon: <MdDelete />,
            tone: 'danger',
            onSelect: () => setDeleting(user),
          },
        ];

        return (
          <DropdownMenu
            aria-label={`Actions sur l’accès de ${user.prenom} ${user.nom}`}
            trigger={<MdMoreVert className="size-5" />}
            triggerClassName="flex size-9 items-center justify-center rounded-lg text-content-muted transition-colors hover:bg-surface-muted hover:text-content"
            items={items}
          />
        );
      },
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        icon={<MdGroups />}
        title="Mon équipe"
        description="Le compte principal du cabinet et les accès que vous avez ouverts pour votre équipe."
        actions={
          estAdmin ? (
            <Button icon={<MdPersonAdd className="size-5" />} onClick={() => setCreating(true)}>
              Ajouter un accès
            </Button>
          ) : null
        }
      />

      {error ? <Alert tone="danger">{errorMessage(error, 'Chargement impossible.')}</Alert> : null}
      {actionError ? <Alert tone="danger">{actionError}</Alert> : null}

      <DataTable
        data={acces}
        columns={columns}
        isLoading={isPending}
        rowId={(user) => user.id}
        rowClassName={(user) => (user.actif ? '' : 'opacity-60')}
        // Le compte responsable y figure toujours : ce message ne s'affiche
        // qu'avant la première réponse de l'API, ou si elle revient vide.
        emptyMessage="Aucun accès à afficher."
      />

      <AccessModal open={creating} onClose={() => setCreating(false)} />

      {/* `key` force un formulaire neuf par accès : sans elle, les valeurs par
          défaut du précédent resteraient affichées. */}
      {editing ? (
        <AccessModal key={editing.id} open user={editing} onClose={() => setEditing(null)} />
      ) : null}

      <DeleteAccessModal user={deleting} onClose={() => setDeleting(null)} />
    </div>
  );
}
